import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { Annotation, Measurement } from "~/types/annotations";
import type { Rectangle } from "~/types/defects";
import { setAsset } from "./assetSlice";

import { fetchSurvey } from "~/thunks/surveyThunks";
import { getAiDetectionsThunk, updateAnnotationThunk } from "~/thunks/annotationsThunks";
import type { RootState } from "~/store";
import { createAnnotationThunk } from "~/thunks/annotationsThunks";
import { genericDeleteThunk } from "~/thunks/deleteThunks";
import { deleteEntitiesByProperty } from "~/components/cesiumComponents/CesiumViewerStore";
import { createModelAndElementsThunk } from "~/thunks/createThunks";


interface AnnotationsManagerState {
  rectangle: Rectangle | null;
  selectedAnnotationsIds: Annotation['id'][];
  featuredAnnotationsIds: Annotation['id'][];
  annotationProcessStarted: boolean;
  imageAnnotations: Annotation[];
  aiDetections: any[];
  editedAnnotation: Partial<Annotation> | null;
  annotationsType: Annotation['annotation_type'];
  aiDetectionsLoaded: boolean;
  searchInput?: string;
};

const initialState: AnnotationsManagerState = {
  rectangle: null,
  selectedAnnotationsIds: [],
  featuredAnnotationsIds: [],
  annotationProcessStarted: false,
  imageAnnotations: [],
  aiDetections: [],
  editedAnnotation: null,
  annotationsType: 'general',
  aiDetectionsLoaded: false,
  searchInput: '',
};


const annotationsManagerSlice = createSlice({
  name: "annotationsManager",
  initialState,
  reducers: {
    setRectangle: (state, action: PayloadAction<{ x: number; y: number; width: number; height: number } | null>) => {
      state.rectangle = action.payload;
    },
    setSearchInput: (state, action: PayloadAction<string>) => {
      state.searchInput = action.payload;
    },
    setSelectedAnnotationsIds: (state, action: PayloadAction<any[]>) => {
      state.selectedAnnotationsIds = action.payload;
    },
    setAnnotationsType: (state, action: PayloadAction<Annotation['annotation_type']>) => {
      state.annotationsType = action.payload;
    },
    setFeaturedAnnotationsIds: (state, action: PayloadAction<any[]>) => {
      state.featuredAnnotationsIds = action.payload;
      state.searchInput = ''; // Reset search input when featured annotations change
    },
    setAnnotationProcessStarted: (state, action: PayloadAction<boolean>) => {
      state.annotationProcessStarted = action.payload;
    },
    setImageAnnotations: (state, action: PayloadAction<any[]>) => {
      state.imageAnnotations = action.payload;
    },
    setEditedAnnotation: (state, action: PayloadAction<Partial<Annotation> | null>) => {
      console.log("setEditedAnnotation", action.payload);

      if (state.editedAnnotation && action.payload) {
        state.editedAnnotation = {
          ...state.editedAnnotation,
          ...action.payload,
        };
        console.log("updated editedAnnotation", state.editedAnnotation);
        const index = state.imageAnnotations.findIndex(ann => ann.id === state.editedAnnotation?.id);
        if (index !== -1 && state.editedAnnotation.entity && state.editedAnnotation.entity !== state.imageAnnotations[index].entity) {

          state.imageAnnotations[index] = {
            ...state.imageAnnotations[index],
            entity: state.editedAnnotation.entity,
          };
          console.log("updated imageAnnotation", state.imageAnnotations[index]);
        }
        const updatedFeaturedIds = state.imageAnnotations.filter(ann => ann.entity === state.editedAnnotation?.entity).map(ann => ann.id) as any;
        // console.log("updated featuredAnnotationsIds", updatedFeaturedIds);
        state.featuredAnnotationsIds = updatedFeaturedIds;
      } else {
        state.editedAnnotation = action.payload;
      }

    },
    addMeasurement: (state, action: PayloadAction<Measurement>) => {
      if (state.editedAnnotation) {
        if (!state.editedAnnotation.measurements) {
          state.editedAnnotation.measurements = [action.payload];
        } else {
          state.editedAnnotation.measurements.push(action.payload);
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSurvey.pending, (state, action) => {
        Object.assign(state, initialState);
      })
      .addCase(fetchSurvey.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("fetchSurvey fulfilled", action.payload);
        state.imageAnnotations = action.payload.annotations;
        // state.survey = action.payload.survey;
      }).
      addCase(getAiDetectionsThunk.fulfilled, (state, action: PayloadAction<any[]>) => {
        console.log("getAiDetectionsThunk fulfilled", action.payload);
        state.aiDetections = action.payload;
        state.aiDetectionsLoaded = true;
      })
      .addCase(createAnnotationThunk.fulfilled, (state, action: PayloadAction<Annotation>) => {
        console.log("createAnnotationThunk fulfilled", action.payload);
        state.imageAnnotations.push(action.payload);
        state.editedAnnotation = null;
        state.featuredAnnotationsIds.push(action.payload.id);
        state.selectedAnnotationsIds = [action.payload.id];
        state.rectangle = null;
      })
      .addCase(updateAnnotationThunk.fulfilled, (state, action: PayloadAction<Annotation>) => {
        console.log("updateAnnotationThunk fulfilled", action.payload);
        // if (action.payload) {

        // }
        const index = state.imageAnnotations.findIndex(ann => ann.id === action.payload.id);
        if (index !== -1) {
          state.imageAnnotations[index] = {
            ...state.imageAnnotations[index],
            ...action.payload,
          };
        }

        state.editedAnnotation = null;
        state.rectangle = null;
      })
      .addCase(setAsset, (state, action: PayloadAction<any>) => {
        Object.assign(state, initialState);
      })
      .addCase(genericDeleteThunk.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("genericDeleteThunk fulfilled", action.payload);
        const operations = action.payload;
        // if (operations.length === 0) return;
        operations.forEach((operation: any) => {
          if (operation.table === 'tbl_image_annotations') {
            const annotationsIds = operation.ids;

            state.imageAnnotations = state.imageAnnotations.filter((ann) => !annotationsIds.includes(ann.id));
            state.selectedAnnotationsIds = state.selectedAnnotationsIds.filter((id) => !annotationsIds.includes(id));
            state.featuredAnnotationsIds = state.featuredAnnotationsIds.filter((id) => !annotationsIds.includes(id));
            annotationsIds.forEach((id: Annotation['id']) => {
              deleteEntitiesByProperty('annotationId', id);
            });
          } else if (operation.table === 'tbl_defects_measurements') {
            const measurementsIds = operation.ids;
            state.imageAnnotations = state.imageAnnotations.map((ann) => {
              const filteredMeasurements = ann.measurements.filter((m) => !measurementsIds.includes(m.id));
              return {
                ...ann,
                measurements: filteredMeasurements,
              };
            });
            measurementsIds.forEach((id: Measurement['id']) => {
              deleteEntitiesByProperty('measurementId', id);
            });
          }


        })
      })
      .addCase(createModelAndElementsThunk.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("createModelAndElementsThunk fulfilled", action.payload);
        if (action.payload && action.payload.deleteAnnotationIds && action.payload.deleteAnnotationIds.length > 0) {
          const filteredAnnotations = state.imageAnnotations.filter(
            ann => !action.payload.saveData.deleteAnnotationIds.includes(ann.id),
          );
          state.imageAnnotations = filteredAnnotations;
          state.selectedAnnotationsIds = state.selectedAnnotationsIds.filter((id) => !action.payload.saveData.deleteAnnotationIds.includes(id));
          state.featuredAnnotationsIds = state.featuredAnnotationsIds.filter((id) => !action.payload.saveData.deleteAnnotationIds.includes(id));
        }
      });
  },
});


export const {
  setRectangle,
  setSelectedAnnotationsIds,
  setFeaturedAnnotationsIds,
  setAnnotationProcessStarted,
  setImageAnnotations,
  setEditedAnnotation,
  addMeasurement,
  setSearchInput,
  setAnnotationsType,
} = annotationsManagerSlice.actions;

export const selectRectangle = (state: RootState) => state.annotationsManager.rectangle;
export const selectSelectedAnnotationsIds = (state: RootState) => state.annotationsManager.selectedAnnotationsIds;
export const selectFeaturedAnnotationsIds = (state: RootState) => state.annotationsManager.featuredAnnotationsIds;
export const selectAnnotationProcessStarted = (state: RootState) => state.annotationsManager.annotationProcessStarted;
export const selectImageAnnotations = (state: RootState) => state.annotationsManager.imageAnnotations;
export const selectAiDetections = (state: RootState) => state.annotationsManager.aiDetections;
export const selectEditedAnnotation = (state: RootState) => state.annotationsManager.editedAnnotation;
export const selectGeneralAnnotations = (state: RootState) => state.annotationsManager.imageAnnotations.filter(ann => ann.annotation_type !== 'defects');
export const selectAiDetectionsLoaded = (state: RootState) => state.annotationsManager.aiDetectionsLoaded;
export const selectSearchInput = (state: RootState) => state.annotationsManager.searchInput;
export const selectAnnotationsType = (state: RootState) => state.annotationsManager.annotationsType;

export default annotationsManagerSlice.reducer;